<template>
    <main class="layout__main layout__main--no-top-padding">
        <transition name="fade">
            <BaseError
                v-if="error || notFound"
                :error="error || { statusCode: 404 }"
            />
        </transition>

        <article
            v-if="!error && !notFound"
            class="bleed"
        >
            <BaseSection
                v-if="pending || tag"
                class="section--wide"
            >
                <template #title>
                    <h1>
                        <template v-if="tagTitle">Tag: {{ tagTitle }}</template>
                        <template v-else>
                            <ContentPlaceholdersHeading />
                        </template>
                    </h1>
                </template>
            </BaseSection>

            <BaseSection
                v-if="featureFlag('tags-news')"
                class="section--wide section--extra-spacing"
            >
                <template #title>
                    <h2>News</h2>
                </template>

                <template #action>
                    <NuxtLink
                        to="/news"
                        aria-label="See all news"
                    >
                        See all <BaseIcon icon="chevron-right" />
                    </NuxtLink>
                </template>

                <template #default>
                    <transition name="fade">
                        <RelatedNews
                            v-if="tag"
                            :related-to-tag-id="tag?.id"
                        />
                        <BaseCardCollection
                            v-else
                            :placeholder="pending"
                            :cards="[]"
                        />
                    </transition>
                </template>
            </BaseSection>

            <BaseSection
                v-if="featureFlag('tags-education')"
                class="section--wide section--extra-spacing"
            >
                <template #title>
                    <h2>Start in your own time</h2>
                </template>

                <template #action>
                    <NuxtLink
                        to="/education-events/education"
                        aria-label="See all on-demand education"
                    >
                        See all <BaseIcon icon="chevron-right" />
                    </NuxtLink>
                </template>

                <template #default>
                    <transition name="fade">
                        <RelatedEvents
                            v-if="tag"
                            :related-to-tag-id="tag?.id"
                            event-type="ondemand"
                        />
                        <BaseCardCollection
                            v-else
                            :placeholder="pending"
                            :cards="[]"
                        />
                    </transition>
                </template>
            </BaseSection>

            <BaseSection
                v-if="featureFlag('tags-events')"
                class="section--wide section--extra-spacing"
            >
                <template #title>
                    <h2>Upcoming events</h2>
                </template>

                <template #action>
                    <NuxtLink
                        to="/education-events/events"
                        aria-label="See all events"
                    >
                        See all <BaseIcon icon="chevron-right" />
                    </NuxtLink>
                </template>

                <template #default>
                    <transition name="fade">
                        <RelatedEvents
                            v-if="tag"
                            :related-to-tag-id="tag?.id"
                            event-type="live"
                        />
                        <BaseCardCollection
                            v-else
                            :placeholder="pending"
                            :cards="[]"
                        />
                    </transition>
                </template>
            </BaseSection>

            <BaseSection
                v-if="featureFlag('tags-sections')"
                class="section--wide section--extra-spacing"
            >
                <template #title>
                    <h2>Sections</h2>
                </template>

                <template #action>
                    <NuxtLink
                        to="/sections"
                        aria-label="See all sections"
                    >
                        See all <BaseIcon icon="chevron-right" />
                    </NuxtLink>
                </template>

                <template #default>
                    <RelatedSections :related-to-tag-id="tag?.id" />
                </template>
            </BaseSection>

            <BaseSection
                v-if="featureFlag('tags-programmes')"
                class="section--wide section--extra-spacing"
            >
                <template #title>
                    <h2>Programmes</h2>
                </template>

                <template #default>
                    <RelatedBooks :related-to-tag-id="tag?.id" />
                </template>
            </BaseSection>
        </article>
    </main>
</template>

<script setup>
import tagQuery from '~/graphql/queries/tags/Tag.graphql';

const { activeSite, featureFlag } = useMultisite();
const { variables } = useCraftGraphqlSlug();

const response = await useLazyAsyncQuery({
    query: tagQuery,
    variables
});

// Todo: SEO Title overwrite: `Tag: ${tagTitle.value} - Uroweb`,
const {
    craftEntry: tag,
    pending,
    error
} = usePageQuery(
    response,
    { entryKey: 'tag', activeSite }
);

const tagTitle = computed(() => {
    return tag.value
        ? tag.value?.title.charAt(0).toUpperCase() + tag.value?.title.slice(1)
        : '';
});

</script>
